import { apiClient } from "./client";
import { AttendanceRecord, UserDailyAttendance } from "../types";

export interface MyAttendanceStatus {
  checked_in: boolean;
  checked_out: boolean;
  checkin_time?: string;
  checkout_time?: string;
  effective_eod_time?: string;
  effective_standup_time?: string;
  is_late?: boolean;
  late_minutes?: number;
  total_worked_minutes?: number;
  getting_late?: boolean;
  getting_late_reason?: string;
  // Set when a timed check-in/out went for approval instead of being logged.
  pending_approval?: boolean;
  message?: string;
  // Local date of a session left open on an earlier day (forgot to check out).
  stale_session_date?: string | null;
}

export interface GoalItem {
  id: string;
  description: string;
  date: string;
  is_completed: boolean;
}

export interface CheckinDataResponse {
  already_checked_in: boolean;
  carryover_goals: GoalItem[];
}

export interface CheckoutDataResponse {
  has_active_checkin: boolean;
  todays_goals: GoalItem[];
  // Set when the open session was forgotten on an earlier day (late check-out).
  stale_session_date?: string | null;
  stale_checkin_time?: string | null;
  stale_default_time?: string | null;
}

export async function getMyAttendance(): Promise<MyAttendanceStatus> {
  const res = await apiClient.get("/attendance/me");
  return res.data;
}

export async function getCheckinData(): Promise<CheckinDataResponse> {
  const res = await apiClient.get("/attendance/checkin-data");
  return res.data;
}

export async function checkin(data: { carryover_goal_ids: string[]; new_goals: string[]; wfh?: boolean; at_time?: string; reason?: string }): Promise<MyAttendanceStatus> {
  const res = await apiClient.post("/attendance/checkin", data);
  return res.data;
}

export async function getCheckoutData(): Promise<CheckoutDataResponse> {
  const res = await apiClient.get("/attendance/checkout-data");
  return res.data;
}

export async function checkout(data: { completed_goal_ids: string[]; additional_completed: string[]; early_leave_reason?: string; at_time?: string; late_reason?: string }): Promise<MyAttendanceStatus> {
  const res = await apiClient.post("/attendance/checkout", data);
  return res.data;
}

export interface MyAttendanceRecord {
  date: string;
  checkin_time?: string;
  checkout_time?: string;
  status: string;
  duration_minutes?: number;
}

export async function getMyHistory(start: string, end: string): Promise<MyAttendanceRecord[]> {
  const res = await apiClient.get("/attendance/my-history", { params: { start, end } });
  return res.data;
}

export async function getAttendance(): Promise<AttendanceRecord[]> {
  const res = await apiClient.get("/attendance");
  return res.data;
}

// Staff: a specific user's raw attendance records (most recent first).
export async function listUserAttendance(userId: string, perPage = 30): Promise<AttendanceRecord[]> {
  const res = await apiClient.get("/attendance", { params: { user_id: userId, per_page: perPage } });
  return res.data;
}

export async function getDailyAttendance(date: string): Promise<UserDailyAttendance[]> {
  const res = await apiClient.get("/attendance/daily", { params: { date } });
  return res.data;
}

// Day-by-day breakdown incl. absent/leave/off/holiday. `userId` may be a UUID
// or Employee ID. Self, staff, or the user's manager.
export interface DailyBreakdownDay {
  date: string;
  status: string;
  worked_minutes: number;
  worked_hours: number;
  expected_minutes: number | null;
  /** The scheduled shift window for this day, e.g. "9:00 AM" / "5:00 PM" — null when not scheduled. */
  expected_start: string | null;
  expected_end: string | null;
  leave_type: string | null;
  is_late: boolean;
  late_minutes: number;
  /** Minutes checked out before the scheduled shift end (0 when not early / not applicable). */
  early_checkout_minutes: number;
  checkin_time: string | null;
  checkout_time: string | null;
  is_active: boolean;
}

export interface DailyBreakdown {
  name: string;
  employee_id: string | null;
  department: string | null;
  designation: string | null;
  role: string;
  present_days: number;
  absent_days: number;
  leave_days: number;
  wfh_days: number;
  late_days: number;
  early_left_days: number;
  overtime_days: number;
  holiday_days: number;
  off_days: number;
  scheduled_working_days: number;
  expected_minutes: number;
  worked_hours: number;
  overtime_hours: number;
  attendance_rate: number;
  days: DailyBreakdownDay[];
}

export async function getDailyBreakdown(
  userId: string, start?: string, end?: string,
): Promise<DailyBreakdown> {
  const res = await apiClient.get(`/attendance/daily/${userId}`, { params: { start, end } });
  return res.data;
}

/** One day in the caller's own attendance breakdown (/attendance/me/overview). */
export interface MyAttendanceDay {
  date: string
  status: 'completed' | 'overtime' | 'less_hours' | 'working' | 'wfh' | 'leave' | 'absent' | 'holiday' | 'off'
  worked_minutes: number
  worked_hours: number
  /** null on holidays, days off and approved leave days (nothing owed). */
  expected_minutes: number | null
  /** This day's own shortfall (0 when not owed or still in progress). */
  short_minutes?: number
  expected_start: string | null
  expected_end: string | null
  leave_type: string | null
  is_late: boolean
  late_minutes: number
  early_checkout_minutes: number
  checkin_time: string | null
  checkout_time: string | null
  is_active: boolean
}

/** The caller's own attendance summary for a date range — same numbers HR sees. */
export interface MyAttendanceOverview {
  start: string
  end: string
  /** First day with a record (later of joining date / first punch or leave); null if none. */
  tracked_from: string | null
  scheduled_working_days: number
  expected_minutes: number
  worked_minutes: number
  short_minutes: number
  present_days: number
  absent_days: number
  leave_days: number
  wfh_days: number
  missing_days: number
  off_days: number
  holiday_days: number
  late_days: number
  late_minutes: number
  early_left_days: number
  overtime_minutes: number
  days: MyAttendanceDay[]
}

export async function getMyAttendanceOverview(start: string, end: string): Promise<MyAttendanceOverview> {
  const res = await apiClient.get<MyAttendanceOverview>('/attendance/me/overview', { params: { start, end } })
  return res.data
}
